import { useCallback } from 'react';
import { FieldValues, UseFormSetValue } from 'react-hook-form';

interface IGetCopyFunction {
  setFormValue: UseFormSetValue<FieldValues>;
}

type Coords = { latitude: string; longitude: string };

const COORD_RE = /-?\d+(?:[.,]\d+)?/g;

export const useGetCopyFunction = ({ setFormValue }: IGetCopyFunction) => {
  const pureCoordinate = (raw: string) => {
    const s = String(raw ?? '').trim();
    const match = s.match(/-?\d+(?:[.,]\d+)?/);

    if (!match) return '';
    return match[0].replace(',', '.');
  };

  const splitCoords = (raw: string): Coords => {
    const value = raw ?? '';

    if (value.includes(',')) {
      const commaIndex = value.indexOf(',');
      const left = value.slice(0, commaIndex);
      const right = value.slice(commaIndex + 1);

      return {
        latitude: pureCoordinate(left),
        longitude: pureCoordinate(right),
      };
    }

    const matches = (value.match(COORD_RE) ?? []) as string[];

    if (matches.length === 0) return { latitude: '', longitude: '' };
    if (matches.length === 1) {
      return { latitude: pureCoordinate(matches[0]), longitude: '' };
    }

    const [latRaw, lonRaw] = matches;

    return {
      latitude: pureCoordinate(latRaw),
      longitude: pureCoordinate(lonRaw),
    };
  };

  const handleLatitudeChange = useCallback(
    (_name?: any, value?: string) => {
      if (!value?.includes(',')) return;

      const { latitude, longitude } = splitCoords(value);

      setFormValue('latitude', latitude, {
        shouldDirty: true,
        shouldValidate: true,
      });

      setFormValue('longitude', longitude, {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    [setFormValue]
  );

  return { handleLatitudeChange };
};

export function parseAddress(addressString = '') {
  const parts = addressString
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean);

  const patterns = {
    stateZip: /^[A-Z]{2}\s+\d{5}(-\d{4})?$/i,
    address2:
      /^(apt|apartment|suite|ste|unit|floor|fl|room|rm|#|building|bldg)/i,
    streetSuffix: /\b(st|ave|rd|blvd|ln|ct|dr|pl|way|pkwy|hwy|sq|ter)\.?\b/i,
  };

  let result = {
    businessName: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zipCode: '',
  };

  const unknowns = parts.filter((part) => {
    if (patterns.stateZip.test(part)) {
      const [state, zip] = part.split(/\s+/);
      result.state = state.toUpperCase();
      result.zipCode = zip;
      return false;
    }
    if (patterns.address2.test(part)) {
      result.address2 = part;
      return false;
    }
    return true;
  });

  if (unknowns.length >= 2) {
    result.city = unknowns.pop() as string;
    result.address1 = unknowns.pop() as string;
    result.businessName = unknowns.join(', ');
  } else if (unknowns.length === 1) {
    result.address1 = unknowns[0];
  }

  return result;
}
